import React, {
    useRef,
    useEffect
} from 'react';

import {
    useShellAlert,
    useUpdateShellPrimary,
    useUpdateShellSecondary
} from '../../shell/hooks/shellHooks.js';

import {
    ConfigurationProvider,
    useSetCurationConfigState
} from '../../../hooks/widgetConfiguration.js';

import {
    useLoadLandscapeListeningQuery
} from '../hooks/useListeningQueryPersistence.js';

import {
    SavedTopicContext
} from '../contexts.js';

import SearchWizard from '../components/savedSearch/SearchWizard.jsx';
import RIQLoading from '../../../components/RIQLoading.jsx';
import RIQPageError from '../../../components/RIQPageError.jsx';

import useUnsavedWorkWarning from '../hooks/useUnsavedWorkWarning.jsx';
import useListeningSearchTimePeriod from '../hooks/useListeningSearchTimePeriod.js';

import {
    listeningDefinitions
} from '../components/enums/listeningDefinitions.jsx';

import useHasCompanionMode from '../hooks/useHasCompanionMode.js';
import useHasValidQuidMonitorToken from '../hooks/useHasValidQuidMonitorToken.js';
import InvalidQuidMonitorTokenErrorPage from '../components/InvalidQuidMonitorTokenErrorPage.jsx';

function CurationLayout({isSampleTopic, title}) {
    const hasShownAlert = useRef(true);
    const {info: alertInfo} = useShellAlert();
    const setTimePeriod = useSetCurationConfigState(null, 'timePeriod');

    const {
        description: timePeriodDescription,
        message: timePeriodMessage,
        override: timePeriodOverride,
        period: timePeriod,
        restrictions: dateRangeRestrictions
    } = useListeningSearchTimePeriod(isSampleTopic, false);

    useEffect(() => {
        if (hasShownAlert.current && timePeriodOverride) {
            hasShownAlert.current = false;
            setTimePeriod(timePeriodOverride);
            alertInfo(listeningDefinitions.shell.timePeriodChange(timePeriod));
        }
    }, [alertInfo, setTimePeriod, timePeriod, timePeriodOverride]);

    useUpdateShellPrimary(title, {
        companySets: {
            disabled: true
        },
        dataFreshness: {
            disabled: true
        },
        timePeriodDateRangeRestrictions: dateRangeRestrictions,
        timePeriod: timePeriod,
        timePeriodMessage: timePeriodMessage,
        timePeriodDescription: timePeriodDescription
    });

    useUpdateShellSecondary(false);

    const unsavedWorkModal = useUnsavedWorkWarning();

    const hasCompanionMode = useHasCompanionMode();
    const hasValidQuidMonitorToken = useHasValidQuidMonitorToken();
    if (hasCompanionMode && !hasValidQuidMonitorToken) {
        return <InvalidQuidMonitorTokenErrorPage/>;
    }

    return (
        <>
            <SearchWizard
                isEditingInstantSearch={false}
                isSampleTopic={isSampleTopic}
            />
            {unsavedWorkModal}
        </>
    );
}

export default function ListeningSavedSearchBuilder(props) {

    // If we're editing a savedSearch, listeningQueryId will be the id of that curation config. Load it from the server so that we know what props to default.
    const editingListeningQueryId = props?.editingListeningQueryId;
    const {data: existingCurationConfiguration, isLoading, error: savedSearchLoadError} = useLoadLandscapeListeningQuery(editingListeningQueryId);

    const loadingCuration = isLoading && editingListeningQueryId && !savedSearchLoadError;

    const defaultProps = _.defaults({
        editingListeningQueryId: editingListeningQueryId,
        name: existingCurationConfiguration?.name
    }, existingCurationConfiguration?.definition);

    const configurationSchema = {
        curationPropsConfig: {
            searchTerms: {
                urlPersistence: true
            },
            source: {
                urlPersistence: true
            },
            language: {
                urlPersistence: true
            },
            searchTermsAnd: {
                urlPersistence: true
            },
            searchTermsNot: {
                urlPersistence: true
            },
            excludeDomain: {
                urlPersistence: true
            },
            excludeAuthor: {
                urlPersistence: true
            },
            theme: {
                urlPersistence: true
            }
        }
    };

    const savedSearchDeleted = editingListeningQueryId && !existingCurationConfiguration && !isLoading && !savedSearchLoadError;
    const showBuilder = !savedSearchLoadError && !loadingCuration && !savedSearchDeleted;

    const title = (editingListeningQueryId ? 'Edit Search' : 'Create New Search');
    return (
        <SavedTopicContext.Provider
            value={existingCurationConfiguration}
        >
            {loadingCuration &&
                <RIQLoading/>
            }

            {savedSearchLoadError &&
                <RIQPageError/>
            }

            {savedSearchDeleted &&
                <RIQPageError>
                    <p>
                        We could not find this saved search, perhaps it was deleted?
                    </p>
                </RIQPageError>
            }

            {showBuilder &&
                <ConfigurationProvider
                    defaultProps={defaultProps}
                    configurationSchema={configurationSchema}
                >
                    <CurationLayout
                        isSampleTopic={existingCurationConfiguration?.isSampleTopic}
                        title={title}/>
                </ConfigurationProvider>
            }
        </SavedTopicContext.Provider>
    );
}
